import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Gauge,
  CreditCard,
  CalendarClock,
  ArrowUpCircle,
  ArrowDownCircle,
  XCircle,
  RotateCcw,
  Loader2,
  ShieldCheck,
  Plus,
} from 'lucide-react';
import { useI18n } from '@/i18n/LanguageContext';
import { useAuth } from '@/contexts/AuthContext';
import Seo from '@/components/Seo';
import WatchDemoButton from '@/components/tutorial/WatchDemoButton';
import CreditLedgerCard from '@/components/CreditLedgerCard';
import CreditPackModal from '@/components/CreditPackModal';
import UpgradeOffers from '@/components/UpgradeOffers';
import { useLeadBalance, creditsChanged } from '@/lib/creditsBus';
import { setMembershipCancel } from '@/lib/creditPacksApi';
import { formatPeriodEnd } from '@/lib/leadBalance';
import { canBuyLeads } from '@/lib/roles';
import { supabase } from '@/lib/supabase';
import {
  money,
  planLabel,
  planLeads,
  PLAN_CODES,
  PLAN_PRICES,
  planIsCancelAnytime,
  planTermNote,
} from '@/data/constants';
import { CREDIT_PACKS, packUnitPrice } from '@/data/creditPacks';

/**
 * ───────────────────────────────────────────────────────────────────────────
 * MEMBERSHIP MANAGEMENT
 * ───────────────────────────────────────────────────────────────────────────
 * One page that answers every billing question a contractor can ask:
 * current plan, status, balance, credits used, credits remaining, renewal
 * date, payment history and the full credit ledger — plus upgrade, downgrade,
 * cancel and buy-more-credits.
 *
 * NUMBERS ARE NEVER INVENTED HERE. The balance comes from getLeadBalance()
 * through useLeadBalance(), the same source the header pill and the dashboard
 * card read, so the three can never disagree.
 *
 * CANCEL sets cancel_at_period_end = true server-side. The membership stays
 * active and every remaining credit stays spendable until the period ends.
 */
const Membership: React.FC = () => {
  const { t, path, lang } = useI18n();
  const es = lang === 'es';
  const { profile, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const { balance, refresh } = useLeadBalance(profile?.id);
  const [packOpen, setPackOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState('');
  const [cancelAtEnd, setCancelAtEnd] = useState(false);
  const [subAmount, setSubAmount] = useState(0);

  // Live subscription flags that are not part of the balance shape.
  useEffect(() => {
    let alive = true;
    const load = async () => {
      if (!profile?.id) return;
      const { data } = await supabase
        .from('mkt_subscriptions')
        .select('cancel_at_period_end, amount, status')
        .eq('user_id', profile.id)
        .in('status', ['active', 'past_due'])
        .order('created_at', { ascending: false })
        .limit(1);
      const row = data && data[0];
      if (!alive || !row) return;
      setCancelAtEnd(row.cancel_at_period_end === true);
      setSubAmount(row.amount || 0);
    };
    void load();
    return () => {
      alive = false;
    };
  }, [profile?.id, balance.subscriptionId, notice]);

  // "Credits" in the contractor nav lands on the ledger, not the top of the page.
  useEffect(() => {
    if (!new URLSearchParams(window.location.search).get('tab')) return;
    const id = window.setTimeout(() => document.getElementById('credits')?.scrollIntoView({ behavior: 'smooth' }), 400);
    return () => window.clearTimeout(id);
  }, []);

  /*
   * Signed out → sign in and come straight back. This used to call navigate()
   * DURING render, which React warns about and which could fire twice on a
   * slow phone (two history entries → Back button bounced the user around).
   */
  useEffect(() => {
    if (!authLoading && !profile) {
      navigate(`${path('/login')}?next=${encodeURIComponent(window.location.pathname)}`, { replace: true });
    }
  }, [authLoading, profile, navigate, path]);

  if (!authLoading && !profile) return null;
  if (!authLoading && profile && !canBuyLeads(profile.user_type)) {
    return (
      <div className="mx-auto max-w-3xl px-4 py-16">
        <p className="rounded-2xl bg-slate-50 p-6 text-center text-slate-600">
          {es
            ? 'Las membresías son para contratistas y subcontratistas.'
            : 'Memberships are for contractors and subcontractors.'}
        </p>
      </div>
    );
  }

  const current = balance.planCode;
  const pct = balance.included > 0 ? Math.min(100, Math.round((balance.used / balance.included) * 100)) : 0;

  const doCancel = async (cancel: boolean) => {
    if (busy) return;
    /*
     * ONE STRAY TAP MUST NOT CANCEL A MEMBERSHIP. On a phone the red button
     * sits right next to "Buy credits"; it now asks once before acting.
     */
    if (
      cancel &&
      !window.confirm(
        es
          ? `¿Cancelar su membresía? Seguirá activa y conservará sus créditos hasta el ${formatPeriodEnd(balance.periodEnd, lang)}. Después no se cobra nada.`
          : `Cancel your membership? It stays active and you keep your credits until ${formatPeriodEnd(balance.periodEnd, lang)}. Nothing is charged after that.`,
      )
    ) {
      return;
    }
    setBusy(true);
    setNotice('');
    let r: Awaited<ReturnType<typeof setMembershipCancel>>;
    try {
      r = await setMembershipCancel(cancel);
    } catch {
      setBusy(false);
      setNotice(es ? 'Se perdió la conexión. Intente de nuevo.' : 'The connection dropped. Please try again.');
      return;
    }
    setBusy(false);
    if (!r.ok) {
      setNotice(
        r.error === 'no_active_subscription'
          ? es
            ? 'No hay una membresía activa para cancelar.'
            : 'There is no active membership to cancel.'
          : es
          ? 'No pudimos actualizar la membresía. Intente de nuevo.'
          : 'We could not update the membership. Please try again.'
      );
      return;
    }
    setCancelAtEnd(r.cancelAtPeriodEnd);
    setNotice(
      r.cancelAtPeriodEnd
        ? es
          ? `Cancelación programada. Conserva sus ${r.remaining} crédito(s) hasta el ${formatPeriodEnd(r.periodEnd, lang)}.`
          : `Cancellation scheduled. You keep your ${r.remaining} remaining credit(s) until ${formatPeriodEnd(r.periodEnd, lang)}.`
        : es
        ? 'Su membresía continúa y se renovará normalmente.'
        : 'Your membership continues and will renew normally.'
    );
    await refresh();
  };

  const statusTone =
    balance.status === 'active'
      ? 'bg-emerald-50 text-emerald-700 ring-emerald-200'
      : balance.status === 'past_due'
      ? 'bg-red-50 text-red-700 ring-red-200'
      : 'bg-slate-100 text-slate-600 ring-slate-200';

  return (
    <div className="bg-slate-50">
      <Seo
        title={es ? 'Mi membresía' : 'My membership'}
        description={
          es
            ? 'Administre su plan, créditos, renovación e historial de pagos.'
            : 'Manage your plan, credits, renewal and payment history.'
        }
        noindex
      />
      {/* Tutorial strip: how credits, unlocking and homeowner details work. */}
      <div className="border-b border-slate-200 bg-white">
        <div className="mx-auto flex max-w-5xl flex-col gap-3 px-4 py-4 sm:flex-row sm:items-center sm:justify-between sm:px-6">
          <p className="text-sm font-semibold text-slate-600">
            {es
              ? '¿Dudas sobre créditos, desbloqueos o dónde ver los datos del cliente? Vea el tutorial de 2 minutos.'
              : 'Questions about credits, unlocking, or where homeowner details appear? Watch the 2-minute tutorial.'}
          </p>
          <WatchDemoButton variant="solid" placement="membership" />
        </div>
      </div>

      <div className="mx-auto max-w-6xl px-4 py-10 sm:px-6 lg:px-8">
        <h1 className="text-3xl font-extrabold text-slate-900">
          {es ? 'Mi membresía' : 'My membership'}
        </h1>
        <p className="mt-1 text-slate-500">
          {es
            ? 'Plan, créditos, renovación, pagos y cancelación — todo en un solo lugar.'
            : 'Plan, credits, renewal, payments and cancellation — all in one place.'}
        </p>

        {notice && (
          <p className="mt-5 rounded-xl border border-slate-200 bg-white p-4 text-sm font-semibold text-slate-700">
            {notice}
          </p>
        )}

        <div className="mt-6 grid gap-6 lg:grid-cols-3">
          {/* ── CURRENT PLAN ────────────────────────────────────────────── */}
          <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm lg:col-span-2">
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div>
                <h2 className="flex items-center gap-2 text-sm font-bold uppercase tracking-wide text-slate-400">
                  <Gauge className="h-4 w-4 text-orange-500" />
                  {es ? 'Plan actual' : 'Current plan'}
                </h2>
                <p className="mt-2 text-2xl font-extrabold text-slate-900">
                  {balance.hasSubscription ? planLabel(current, lang) : es ? 'Sin membresía' : 'No membership'}
                  {subAmount > 0 && (
                    <span className="ml-2 text-sm font-semibold text-slate-400">
                      {money(subAmount)}
                      {t('pricing.perMonth')}
                    </span>
                  )}
                </p>
                {balance.hasSubscription && (
                  <span
                    className={`mt-2 inline-flex rounded-full px-2.5 py-1 text-[11px] font-bold uppercase tracking-wide ring-1 ${statusTone}`}
                  >
                    {/* Plain words, never the raw database value ("past_due"). */}
                    {cancelAtEnd
                      ? es
                        ? 'Cancela al final del ciclo'
                        : 'Cancels at period end'
                      : balance.status === 'active'
                      ? es ? 'Activa' : 'Active'
                      : balance.status === 'past_due'
                      ? es ? 'Pago pendiente' : 'Payment needed'
                      : es ? 'Inactiva' : 'Inactive'}
                  </span>
                )}
              </div>
              <div className="text-right">
                <p className="text-5xl font-extrabold leading-none text-slate-900 tabular-nums">
                  {balance.spendable}
                </p>
                <p className="mt-1 text-xs font-bold uppercase tracking-wide text-slate-400">
                  {es ? 'créditos disponibles' : 'credits available'}
                </p>
              </div>
            </div>

            {balance.hasSubscription && (
              <>
                <div className="mt-6 h-2.5 w-full overflow-hidden rounded-full bg-slate-100">
                  <div
                    className={`h-full rounded-full transition-all ${
                      balance.exhausted ? 'bg-red-500' : 'bg-emerald-500'
                    }`}
                    style={{ width: `${pct}%` }}
                  />
                </div>
                <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-4">
                  {[
                    { label: es ? 'Incluidos' : 'Included', v: balance.included },
                    { label: es ? 'Usados' : 'Used', v: balance.used },
                    { label: es ? 'Restantes' : 'Remaining', v: balance.remaining },
                    { label: es ? 'Comprados' : 'Purchased', v: balance.extra },
                  ].map((s) => (
                    <div key={s.label} className="rounded-xl bg-slate-50 p-3 text-center">
                      <p className="text-2xl font-extrabold tabular-nums text-slate-900">{s.v}</p>
                      <p className="mt-0.5 text-[11px] font-bold uppercase tracking-wide text-slate-400">
                        {s.label}
                      </p>
                    </div>
                  ))}
                </div>
                <p className="mt-4 flex items-center gap-2 text-sm font-semibold text-slate-600">
                  <CalendarClock className="h-4 w-4 text-slate-400" />
                  {cancelAtEnd
                    ? es
                      ? 'Acceso hasta'
                      : 'Access until'
                    : es
                    ? 'Se renueva el'
                    : 'Renews on'}
                  : {formatPeriodEnd(balance.periodEnd, lang)}
                </p>
                <p className="mt-1 text-[11px] font-semibold text-amber-700">{t('leads.noRollover')}</p>
                {!planIsCancelAnytime(current) && (
                  <p className="mt-2 text-[11px] leading-relaxed text-slate-500">
                    {planTermNote(current, PLAN_PRICES[current] || subAmount, lang)}
                  </p>
                )}
              </>
            )}

            {/* ── ACTIONS ───────────────────────────────────────────────── */}
            <div className="mt-6 flex flex-wrap gap-3 border-t border-slate-100 pt-5">
              <Link
                to={path('/pricing')}
                className="inline-flex items-center gap-2 rounded-xl bg-slate-900 px-5 py-2.5 text-sm font-bold text-white hover:bg-slate-800"
              >
                <ArrowUpCircle className="h-4 w-4" />
                {balance.hasSubscription ? (es ? 'Cambiar plan' : 'Upgrade / downgrade') : es ? 'Ver planes' : 'View plans'}
              </Link>
              <button
                onClick={() => setPackOpen(true)}
                style={{ touchAction: 'manipulation' }}
                className="inline-flex items-center gap-2 rounded-xl bg-orange-500 px-5 py-2.5 text-sm font-bold text-white hover:bg-orange-600"
              >
                <Plus className="h-4 w-4" /> {es ? 'Comprar créditos' : 'Buy credits'}
              </button>
              {balance.hasSubscription &&
                (cancelAtEnd ? (
                  <button
                    onClick={() => doCancel(false)}
                    disabled={busy}
                    className="inline-flex items-center gap-2 rounded-xl border border-slate-300 px-5 py-2.5 text-sm font-bold text-slate-700 hover:bg-slate-50 disabled:opacity-60"
                  >
                    {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <RotateCcw className="h-4 w-4" />}
                    {es ? 'Reanudar membresía' : 'Resume membership'}
                  </button>
                ) : (
                  <button
                    onClick={() => doCancel(true)}
                    disabled={busy}
                    className="inline-flex items-center gap-2 rounded-xl border border-red-200 px-5 py-2.5 text-sm font-bold text-red-700 hover:bg-red-50 disabled:opacity-60"
                  >
                    {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <XCircle className="h-4 w-4" />}
                    {es ? 'Cancelar membresía' : 'Cancel membership'}
                  </button>
                ))}
            </div>
            <p className="mt-3 text-[11px] leading-relaxed text-slate-400">
              {es
                ? 'Al cancelar, la membresía permanece activa y usted conserva los créditos restantes hasta el final del ciclo de facturación actual. No se cobra nada después.'
                : 'Cancelling keeps the membership active and you keep every remaining credit until the end of the current billing cycle. Nothing is charged afterwards.'}
            </p>
          </div>

          {/* ── PLAN SWITCHER ───────────────────────────────────────────── */}
          <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
            <h2 className="flex items-center gap-2 text-sm font-bold uppercase tracking-wide text-slate-400">
              <CreditCard className="h-4 w-4 text-orange-500" />
              {es ? 'Planes' : 'Plans'}
            </h2>
            <ul className="mt-4 space-y-3">
              {PLAN_CODES.map((code) => {
                const active = balance.hasSubscription && code === current;
                const down = balance.hasSubscription && PLAN_PRICES[code] < (PLAN_PRICES[current] || 0);
                return (
                  <li
                    key={code}
                    className={`rounded-xl border p-3 ${active ? 'border-emerald-300 bg-emerald-50' : 'border-slate-200'}`}
                  >
                    <div className="flex items-center justify-between gap-3">
                      <div>
                        <p className="text-sm font-extrabold text-slate-900">{planLabel(code, lang)}</p>
                        <p className="text-xs font-semibold text-slate-500">
                          {money(PLAN_PRICES[code])}
                          {t('pricing.perMonth')} · {planLeads(code)} {es ? 'créditos' : 'credits'}
                        </p>
                      </div>
                      {active ? (
                        <span className="rounded-full bg-emerald-600 px-2 py-1 text-[10px] font-bold uppercase text-white">
                          {es ? 'Actual' : 'Current'}
                        </span>
                      ) : (
                        <Link
                          to={`${path('/pricing')}?plan=${code}`}
                          className="inline-flex items-center gap-1 rounded-lg border border-slate-300 px-3 py-1.5 text-xs font-bold text-slate-700 hover:bg-slate-50"
                        >
                          {down ? <ArrowDownCircle className="h-3.5 w-3.5" /> : <ArrowUpCircle className="h-3.5 w-3.5" />}
                          {down ? (es ? 'Bajar' : 'Downgrade') : es ? 'Subir' : 'Upgrade'}
                        </Link>
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
            <p className="mt-3 text-[11px] leading-relaxed text-slate-400">
              {es
                ? 'El cambio de plan se completa en el checkout seguro. Los créditos del nuevo plan se acreditan cuando el pago se liquida.'
                : 'Plan changes complete in secure checkout. New plan credits are granted when the payment settles.'}
            </p>
          </div>
        </div>

        {/* ── UPGRADE PATH: Basic -> Pro / Premium, Pro -> Premium ─────────── */}
        {balance.hasSubscription && (
          <div className="mt-6">
            <UpgradeOffers currentPlan={current} placement="membership" />
          </div>
        )}

        {/* ── ZERO BALANCE: PACKS FRONT AND CENTRE ───────────────────────── */}
        {balance.spendable === 0 && (
          <div className="mt-6 rounded-2xl border-2 border-orange-200 bg-orange-50 p-6">
            <h2 className="text-lg font-extrabold text-orange-900">
              {es ? 'No le quedan créditos' : 'You have no credits left'}
            </h2>
            <p className="mt-1 text-sm text-orange-800">
              {es
                ? 'Compre un paquete y desbloquee leads de inmediato. Los créditos nunca se gastan solos.'
                : 'Buy a pack and unlock leads right away. Credits are never spent automatically.'}
            </p>
            <div className="mt-4 grid gap-3 sm:grid-cols-3">
              {CREDIT_PACKS.map((p) => (
                <button
                  key={p.code}
                  onClick={() => setPackOpen(true)}
                  style={{ touchAction: 'manipulation' }}
                  className="rounded-xl border border-orange-300 bg-white p-4 text-left hover:border-orange-500"
                >
                  <p className="text-base font-extrabold text-slate-900">
                    {p.credits} {es ? 'créditos' : 'credits'}
                  </p>
                  <p className="text-sm font-bold text-orange-600">{money(p.amount)}</p>
                  <p className="mt-0.5 text-[11px] font-semibold text-slate-500">
                    {money(packUnitPrice(p))} {es ? 'por lead' : 'per lead'}
                  </p>
                </button>
              ))}
            </div>
          </div>
        )}

        <div id="credits" className="mt-6 grid scroll-mt-24 gap-6 lg:grid-cols-2">

          <CreditLedgerCard userId={profile?.id} balance={balance.spendable} />
          <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
            <h2 className="flex items-center gap-2 text-sm font-bold uppercase tracking-wide text-slate-400">
              <ShieldCheck className="h-4 w-4 text-orange-500" />
              {es ? 'Cómo funcionan los créditos' : 'How credits work'}
            </h2>
            <ul className="mt-4 space-y-3 text-sm leading-relaxed text-slate-600">
              <li>
                <strong className="text-slate-900">1 {es ? 'crédito' : 'credit'} = 1 lead.</strong>{' '}
                {es
                  ? 'Se descuenta únicamente cuando presiona Desbloquear Lead.'
                  : 'It is deducted only when you press Unlock Lead.'}
              </li>
              <li>
                {es
                  ? 'Comprar créditos nunca los gasta. Usted elige el proyecto.'
                  : 'Buying credits never spends them. You choose the project.'}
              </li>
              <li>
                {es
                  ? 'Los créditos del plan expiran al final del ciclo. Los créditos comprados ($29 o paquetes) no expiran con el ciclo.'
                  : 'Plan credits expire at the end of the cycle. Purchased credits ($29 or packs) do not expire with the cycle.'}
              </li>
              <li>
                {es
                  ? 'Primero se usan los créditos del plan, luego los comprados — el orden se aplica en el servidor.'
                  : 'Plan credits are spent first, then purchased credits — the order is enforced server-side.'}
              </li>
            </ul>
            <Link
              to={path('/jobs')}
              onClick={() => creditsChanged('navigate-jobs')}
              className="mt-5 inline-flex rounded-xl border border-slate-300 px-5 py-2.5 text-sm font-bold text-slate-700 hover:bg-slate-50"
            >
              {t('dash.availableJobs')}
            </Link>
          </div>
        </div>
      </div>

      <CreditPackModal open={packOpen} onClose={() => setPackOpen(false)} onCredited={() => void refresh()} />
    </div>
  );
};

export default Membership;
